import { Conditional } from "@@/shared/conditional";
import { VerticalDivider } from "@@/shared/dividers";
import { FlexColumn, FlexRow } from "@@/shared/flex-containers";
import { Icon } from "@@/shared/icons/icon";
import { paddingToCssValue } from "@@/shared/padding";
import { FieldTitle } from "@@/shared/text/field-title";
import { TextBox } from "@@/shared/text/text-box";
import { useTranslate } from "@@/translations/use-translate";
import type { SerializedStyles } from "@emotion/react";
import { useTheme } from "@emotion/react";
import { faSpinnerThird } from "@fortawesome/pro-duotone-svg-icons";
import type { IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import type { ColorSet, Padding, RemSize, Translatable } from "@towni/common";
import type React from "react"; // Import React directly

// --- Data Structure Definitions ---

// Represents a single selectable option
type OptionData<Value extends string | number | undefined = string> = {
    name: Translatable | number;
    value: Value;
    disabledOption?: boolean;
    // Explicitly mark as not a group (optional, for type guards)
    isGroup?: false;
};

// Represents a group of options
type GroupData<Value extends string | number | undefined = string> = {
    label: Translatable | string; // Group label
    options: OptionData<Value>[]; // Options within the group
    disabled?: boolean; // Disable the entire group
    // Explicitly mark as a group (required for type guards)
    isGroup: true;
};

// The data prop can now accept an array of options OR groups
type SelectData<Value extends string | number | undefined = string> = (
    | OptionData<Value>
    | GroupData<Value>
)[];

// Helper type guard to check if an item is a group
function isGroupData<Value extends string | number | undefined>(
    item: OptionData<Value> | GroupData<Value>,
): item is GroupData<Value> {
    return !!(item as GroupData<Value>).isGroup;
}

// --- Props Definition ---

type Props<Value extends string | number | undefined = string> = {
    // Updated data prop type
    readonly data: SelectData<Value>;
    readonly selectedValue: Value | undefined;
    readonly noneValue?: Value; // Consider if this concept still applies with groups
    readonly onSelect: (nameAndValue: OptionData<Value>) => void; // onSelect still returns the specific selected OptionData
    readonly styling?: SerializedStyles;
    readonly containerCss?: SerializedStyles;
    readonly padding?: Padding;
    readonly disabled?: boolean;
    readonly title?: Translatable;
    readonly description?: Translatable;
    readonly spin?: boolean;
    readonly icon?: IconDefinition;
    readonly className?: string;
    readonly fontSize?: RemSize | number;
    readonly iconPlacement?: "left" | "right";
    readonly iconColor?: string;
    readonly iconSize?: RemSize | number;
    readonly hideContainer?: boolean;
    readonly colorSet?: ColorSet;
};

// --- Helper Function to Find Option ---

const findOptionByValue = <Value extends string | number | undefined>(
    data: SelectData<Value>,
    valueToFind: string | undefined, // value from event is always string | undefined
): OptionData<Value> | undefined => {
    if (valueToFind === undefined) return undefined;

    for (const item of data) {
        if (isGroupData(item)) {
            // Search within the group's options
            const foundInGroup = item.options.find(
                opt => opt.value?.toString() === valueToFind,
            );
            if (foundInGroup) {
                return foundInGroup;
            }
        } else {
            // Check standalone option
            if (item.value?.toString() === valueToFind) {
                return item;
            }
        }
    }
    return undefined; // Not found
};

// --- Component Implementation ---

const SelectPickerNative = <Value extends string | number | undefined = string>(
    props: Props<Value>,
) => {
    const theme = useTheme();
    const translate = useTranslate();
    const colorSet = props.colorSet ?? theme.colors.textInput;

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (props.disabled || props.spin) return;

        const selectedValueString = e.currentTarget.value;
        const foundOption = findOptionByValue(props.data, selectedValueString);

        if (foundOption) {
            // Check if the found option itself or its group is disabled
            // Note: A disabled <option> or <optgroup> shouldn't be selectable by the browser,
            // but this adds an extra layer of safety.
            const group = !foundOption.isGroup // find group if option belongs to one (may need enhancement if needed)
                ? (props.data.find(
                      item =>
                          isGroupData(item) &&
                          item.options.some(
                              opt => opt.value === foundOption.value,
                          ),
                  ) as GroupData<Value> | undefined)
                : undefined;

            if (!foundOption.disabledOption && (!group || !group.disabled)) {
                props.onSelect(foundOption);
            }
        }
        // Handle case where nothing is found (e.g., placeholder/initial state) - depends on desired behavior
        // Maybe select the first *available* option if `foundOption` is null?
        // else if (props.data.length > 0) {
        //    Find first available option logic here...
        //    props.onSelect(firstAvailableOption);
        // }
    };

    // Helper function to render a single option
    const renderOption = (
        option: OptionData<Value>,
        key: string | number,
        componentDisabled: boolean,
    ) => {
        const _name =
            typeof option.name === "number"
                ? option.name.toString()
                : translate(option.name);

        return (
            <option
                key={option.value?.toString() ?? `opt-undef-${key}`} // Use value if available, else key
                value={option.value}
                disabled={
                    componentDisabled || // If whole component is disabled
                    option.disabledOption // If this specific option is disabled
                }>
                {_name}
            </option>
        );
    };

    const textColor = colorSet.text.asString;
    const disabled = !!(props.disabled || props.spin);
    const iconPlacement = props.iconPlacement || "right";
    const fontSize = props.fontSize ?? 1;
    const iconSize = props.iconSize ?? fontSize;

    return (
        <FlexColumn fillParentWidth mainAxis="flex-start">
            {/* Title and Description remain the same */}
            {props.title ? (
                <>
                    <FieldTitle
                        text={props.title}
                        css={{ opacity: disabled ? 0.45 : 1 }}
                    />
                    <VerticalDivider XS />
                </>
            ) : null}

            <FlexRow
                crossAxis="center"
                css={[
                    { flex: 1 },
                    props.containerCss ?? {},
                    { position: "relative" },
                ]}>
                {/* Icon Placement Logic remains the same */}
                <Conditional
                    when={
                        iconPlacement === "left" && !!(props.icon || props.spin)
                    }>
                    <Icon
                        icon={props.spin ? faSpinnerThird : props.icon!} // Use non-null assertion if icon is guaranteed when placement is left
                        fixedWidth
                        color={props.iconColor ?? theme.colors.primary.asString}
                        spin={props.spin}
                        size={iconSize}
                        style={{
                            pointerEvents: "none",
                            position: "absolute",
                            fontWeight: 700,
                            marginLeft: 10,
                            zIndex: 10,
                            opacity: disabled ? 0.45 : 1,
                        }}
                    />
                </Conditional>

                <select
                    onChange={handleChange}
                    disabled={disabled}
                    value={props.selectedValue ?? ""} // Ensure value is not undefined for controlled component
                    className={props.className}
                    css={[
                        // Base styling remains the same
                        {
                            WebkitAppearance: "none",
                            width: "100%",
                            color: textColor,
                            backgroundColor: props.hideContainer
                                ? theme.colors.transparent.asString
                                : colorSet.background.asString,
                            textOverflow: "ellipsis",
                            opacity: disabled ? 0.3 : 1,
                            border: props.hideContainer
                                ? "none"
                                : `1px solid ${colorSet.border.asString}`,
                            borderRadius: theme.radius,
                            fontSize: `${fontSize}rem`,
                            padding: paddingToCssValue(
                                props.padding ?? {
                                    // Default padding logic remains the same
                                    top: 10,
                                    bottom: 10,
                                    right:
                                        iconPlacement === "right"
                                            ? 36 * fontSize
                                            : 15,
                                    left:
                                        props.icon && iconPlacement === "left"
                                            ? 36 * fontSize
                                            : 15,
                                },
                            ),
                            cursor: disabled ? "default" : "pointer",
                        },
                        props.styling,
                        {
                            cursor: disabled ? "default" : "pointer", // Redundant? Already above
                        },
                    ]}>
                    {/* --- Updated Rendering Logic --- */}
                    {props.data.map((item, index) => {
                        if (isGroupData(item)) {
                            // Render an Optgroup
                            const groupLabel = translate(item.label);
                            return (
                                <optgroup
                                    key={`group-${groupLabel}-${index}`}
                                    label={groupLabel}
                                    disabled={disabled || item.disabled} // Disable group if component or group itself is disabled
                                >
                                    {/* Render options within the group */}
                                    {item.options.map(
                                        (option, optIndex) =>
                                            renderOption(
                                                option,
                                                `${index}-${optIndex}`,
                                                disabled || !!item.disabled,
                                            ), // Pass component and group disabled status
                                    )}
                                </optgroup>
                            );
                        } else {
                            // Render a standalone option
                            return renderOption(item, index, disabled); // Pass only component disabled status
                        }
                    })}
                    {/* --- End Updated Rendering Logic --- */}
                </select>

                {/* Icon Placement Logic remains the same */}
                <Conditional when={iconPlacement === "right"}>
                    <Icon
                        icon={
                            props.spin
                                ? faSpinnerThird
                                : (props.icon ?? faChevronDown)
                        }
                        fixedWidth
                        size={iconSize}
                        color={props.iconColor ?? theme.colors.primary.asString}
                        opacity={disabled ? 0.5 : 1}
                        spin={props.spin}
                        style={{
                            pointerEvents: "none",
                            position: "relative", // Was absolute, check if relative is correct
                            fontWeight: 700,
                            marginLeft: iconSize > fontSize ? -35 : -30, // Adjust based on icon size
                            zIndex: 10,
                        }}
                    />
                </Conditional>
            </FlexRow>

            {/* Description remains the same */}
            {props.description ? (
                <>
                    <VerticalDivider XS />
                    <TextBox
                        text={props.description}
                        color={theme.colors.black.withAlpha(0.5).asString}
                        css={{ padding: 0 }}
                        size={0.8}
                    />
                </>
            ) : null}
        </FlexColumn>
    );
};

export { SelectPickerNative };
export type { GroupData, OptionData as KeyValue, OptionData, SelectData }; // Export new types
